<multi-select>
    <div class="relative flex w-full">
        <!-- Trigger -->
        <button
                type="button"
                id="{state.triggerId}"
                class="btn btn-ghost w-full flex justify-between items-center select select-bordered text-sm font-medium cursor-pointer disabled:cursor-not-allowed no-animation truncate w-full min-w-0"
                disabled="{props.disabled}"
                aria-haspopup="listbox"
                aria-expanded="{state.open}"
                aria-controls="{state.listboxId}"
                aria-labelledby="{props.labelledby || null}"
                onclick="{toggleDropdown}"
                onkeydown="{handleTriggerKeydown}"
        >
      <!-- Placeholder greys out to match single-select's, which the two filter
           bars show side by side; selected values keep the normal weight and
           colour so a chosen filter still reads as chosen. -->
      <span class="text-start truncate overflow-hidden {state.selectedValues.length ? '' : 'text-gray-500'}">
        <virtual if="{state.selectedValues.length}">
          <span each="{sel in selectedDisplay()}" class="ms-sel {sel.color || ''}">{sel.label}</span>
        </virtual>
        <virtual if="{!state.selectedValues.length}">{props.placeholder || locale?.t('all')}</virtual>
      </span>

        </button>

        <!-- Dropdown -->
        <div if="{state.open}"
             class="bg-base-100 border border-base-300 rounded-box mt-2 overflow-hidden shadow-md absolute z-10 flex flex-col w-full min-w-56 start-0 max-h-100"
             style="top: 100%;">
            <div class="sticky top-0 bg-base-100 px-3 py-2 border-b border-base-300">
                <div class="relative">
                    <input
                        type="text"
                        id="{state.searchId}"
                        aria-label="{locale?.t('search')}"
                        placeholder="{locale?.t('search')}"
                        value="{state.searchText}"
                        oninput="{handleSearch}"
                        onclick="{(e) => e.stopPropagation()}"
                        onkeydown="{handleSearchKeydown}"
                        class="input input-sm input-bordered w-full pe-8"
                    />
                    <button type="button" if="{state.searchText}"
                            class="absolute inset-y-0 end-2 z-10 flex items-center text-gray-500 hover:text-primary cursor-pointer"
                            aria-label="{locale?.t('clearsearch')}"
                            tabindex="-1"
                            onclick="{clearSearch}">
                        <i class="icon-clear" aria-hidden="true"></i>
                    </button>
                </div>
            </div>
            <ul role="listbox"
                aria-multiselectable="true"
                aria-labelledby="{props.labelledby || null}"
                id="{state.listboxId}"
                class="overflow-y-auto flex flex-col w-full max-h-96 outline-none"
                tabindex="-1">
                <li each="{(item, idx) in state.filteredOptions}"
                    role="option"
                    aria-selected="{state.selectedValues.includes(item.value)}"
                    tabindex="{state.activeIndex === idx ? 0 : -1}"
                    id="{state.listboxId}-option-{idx}"
                    onkeydown="{(e) => onOptionKeydown(e, item, idx)}"
                    class="w-full hover:bg-base-200 focus:bg-base-200 focus:outline-none cursor-pointer">
                    <label class="flex items-center gap-2 w-full px-3 py-2 {item.color || 'text-primary'} cursor-pointer">
                        <span class="flex-1 break-words text-sm font-medium">{item.label}</span>
                        <input
                            id="{state.listboxId}-checkbox-{idx}"
                            type="checkbox"
                            aria-hidden="true"
                            tabindex="-1"
                            checked="{state.selectedValues.includes(item.value)}"
                            onchange="{() => toggleOption(item)}"
                            class="bg-white w-5 h-5 mt-1 shrink-0 cursor-pointer"
                        />
                    </label>
                </li>
                <li if="{state.filteredOptions.length === 0}"
                    class="px-3 py-2 text-sm text-gray-500 italic"
                    aria-disabled="true">
                    {locale?.t('no_results')}
                </li>
            </ul>
        </div>
    </div>

    <script>
        // Module-scope counter for generating unique IDs across instances.
        // Each multi-select needs unique IDs for aria-controls/aria-labelledby
        // wiring; using a counter avoids collisions when the same page renders
        // multiple multi-selects.
        let multiSelectInstanceCounter = 0;

        export default {
            includeTools: false,

            state: {
                open: false,
                selectedValues: [],
                selectedLabels: [],
                options: [],
                searchText: '',
                filteredOptions: [],
                initialSelectedValues: [], // Snapshot when dropdown opens
                activeIndex: -1, // -1 = none; otherwise idx into filteredOptions
                triggerId: '',
                searchId: '',
                listboxId: ''
            },

            props: {
                preselected: null,
                labelledby: null,
                colorproperty: null // optional key holding a text-color class per item (e.g. status/action colors)
            },

            onBeforeMount() {
                const n = ++multiSelectInstanceCounter;
                this.state.triggerId = 'multi-select-' + n + '-trigger';
                this.state.searchId = 'multi-select-' + n + '-search';
                this.state.listboxId = 'multi-select-' + n + '-listbox';
            },

            emitIfChanged() {
                const hasChanged = JSON.stringify(this.state.selectedValues) !== JSON.stringify(this.state.initialSelectedValues);
                if (hasChanged && this.props.onupdated) {
                    this.props.onupdated(this.state.selectedValues);
                }
            },

            // Selected values resolved to { label, color } for the trigger summary,
            // so each chip can carry its status/action color when colorproperty is set.
            selectedDisplay() {
                return this.state.selectedValues.map(v => {
                    const o = this.state.options.find(op => op.value === v);
                    return { label: o ? o.label : v, color: o ? (o.color || '') : '' };
                });
            },

            toggleDropdown() {
                if (this.props.disabled) return;
                if (this.state.open) {
                    this.closeDropdown(false);
                } else {
                    this.openDropdown(null);
                }
            },

            // initialFocus: 'search' | 'first' | 'last' | null
            openDropdown(initialFocus) {
                this.update({
                    open: true,
                    initialSelectedValues: [...this.state.selectedValues],
                    activeIndex: -1
                });
                if (initialFocus === 'first' && this.state.filteredOptions.length > 0) {
                    this.focusOption(0);
                } else if (initialFocus === 'last' && this.state.filteredOptions.length > 0) {
                    this.focusOption(this.state.filteredOptions.length - 1);
                } else {
                    this.focusSearch();
                }
            },

            closeDropdown(restoreFocus) {
                this.update({
                    open: false,
                    searchText: '',
                    filteredOptions: this.state.options,
                    activeIndex: -1
                });
                this.emitIfChanged();
                if (restoreFocus) {
                    setTimeout(() => {
                        const trigger = document.getElementById(this.state.triggerId);
                        if (trigger) trigger.focus();
                    }, 0);
                }
            },

            focusSearch() {
                setTimeout(() => {
                    const el = document.getElementById(this.state.searchId);
                    if (el) el.focus();
                }, 0);
            },

            focusOption(idx) {
                this.update({ activeIndex: idx });
                setTimeout(() => {
                    const el = document.getElementById(this.state.listboxId + '-option-' + idx);
                    if (el) el.focus();
                }, 0);
            },

            handleSearch(e) {
                const searchText = e.target.value;
                const filteredOptions = this.state.options.filter(option =>
                    option.label.toLowerCase().includes(searchText.toLowerCase())
                );
                // Reset activeIndex when filter changes — the old index may
                // be out of range for the new filteredOptions.
                this.update({ searchText, filteredOptions, activeIndex: -1 });
            },

            clearSearch() {
                this.update({ searchText: '', filteredOptions: this.state.options, activeIndex: -1 });
                this.focusSearch();
            },

            handleTriggerKeydown(e) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    if (this.props.disabled) return;
                    if (!this.state.open) {
                        this.openDropdown('first');
                    } else if (this.state.filteredOptions.length > 0) {
                        this.focusOption(0);
                    }
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (this.props.disabled) return;
                    if (!this.state.open) {
                        this.openDropdown('last');
                    } else if (this.state.filteredOptions.length > 0) {
                        this.focusOption(this.state.filteredOptions.length - 1);
                    }
                } else if (e.key === 'Escape' && this.state.open) {
                    e.preventDefault();
                    this.closeDropdown(true);
                }
            },

            handleSearchKeydown(e) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    if (this.state.filteredOptions.length > 0) {
                        this.focusOption(0);
                    }
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    this.closeDropdown(true);
                } else if (e.key === 'Tab') {
                    // Tab out of the dropdown closes it but doesn't restore
                    // focus (let Tab move to next natural focusable).
                    this.closeDropdown(false);
                }
            },

            onOptionKeydown(e, item, idx) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    if (idx < this.state.filteredOptions.length - 1) {
                        this.focusOption(idx + 1);
                    }
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (idx === 0) {
                        // First option → move focus back up to the search input.
                        this.focusSearch();
                    } else {
                        this.focusOption(idx - 1);
                    }
                } else if (e.key === 'Home') {
                    e.preventDefault();
                    this.focusOption(0);
                } else if (e.key === 'End') {
                    e.preventDefault();
                    this.focusOption(this.state.filteredOptions.length - 1);
                } else if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    this.toggleOption(item);
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    this.closeDropdown(true);
                } else if (e.key === 'Tab') {
                    this.closeDropdown(false);
                }
            },

            toggleOption(item) {
                const selectedValues = [...this.state.selectedValues]
                const selectedLabels = [...this.state.selectedLabels]
                const idx = selectedValues.indexOf(item.value)

                if (idx >= 0) {
                    selectedValues.splice(idx, 1)
                    selectedLabels.splice(idx, 1)
                } else {
                    selectedValues.push(item.value)
                    selectedLabels.push(item.label)
                }

                this.update({ selectedValues, selectedLabels })
            },

            onClickOutside(e) {
                if (!this.root.contains(e.target) && this.state.open) {
                    // Click-outside close: don't restore focus to trigger,
                    // since the user clicked something else.
                    this.closeDropdown(false);
                }
            },

            onMounted() {
                this.initializeOptions();
                document.addEventListener('click', this.onClickOutside)
            },

            onBeforeUpdate() {
                // Handle prop changes after mount
                const idProp = this.props.idproperty || 'id';
                const displayProp = this.props.displayproperty || 'displayName';

                // Rebuild options if list changed
                const colorProp = this.props.colorproperty;
                const newOptions = this.props.list.map(i => ({
                    label: i[displayProp],
                    value: i[idProp],
                    color: colorProp ? (i[colorProp] || '') : ''
                }));

                // Check if options changed
                if (JSON.stringify(newOptions) !== JSON.stringify(this.state.options)) {
                    this.state.options = newOptions;
                    this.state.filteredOptions = newOptions;
                }

                // Only re-apply preselected when the parent has passed a DIFFERENT
                // preselected than we saw last — otherwise we'd overwrite the
                // user's local selections on every parent re-render.
                const preselectedSnapshot = JSON.stringify(this.props.preselected || []);
                if (preselectedSnapshot !== this.state.lastPreselectedSnapshot) {
                    this.state.lastPreselectedSnapshot = preselectedSnapshot;
                    const pre = this.props.preselected || [];
                    this.state.selectedValues = pre.map(item => item[idProp]);
                    this.state.selectedLabels = pre.map(item => item[displayProp]);
                }
            },

            initializeOptions() {
                const idProp = this.props.idproperty || 'id';
                const displayProp = this.props.displayproperty || 'displayName';
                const colorProp = this.props.colorproperty;

                this.state.options = this.props.list.map(i => ({
                    label: i[displayProp],
                    value: i[idProp],
                    color: colorProp ? (i[colorProp] || '') : ''
                }));

                // Initialize filteredOptions with all options
                this.state.filteredOptions = this.state.options;

                if (this.props.preselected && this.props.preselected.length) {
                    const selectedValues = this.props.preselected.map(item => item[idProp]);
                    const selectedLabels = this.props.preselected.map(item => item[displayProp]);

                    this.state.selectedValues = selectedValues;
                    this.state.selectedLabels = selectedLabels;
                } else {
                    this.state.selectedValues = [];
                    this.state.selectedLabels = [];
                }

                this.state.lastPreselectedSnapshot = JSON.stringify(this.props.preselected || []);
            },

            onBeforeUnmount() {
                document.removeEventListener('click', this.onClickOutside)
            }
        }
    </script>

    <style>
        /* Comma-separate the selected labels in the trigger summary so each label
           keeps its own color instead of being flattened into one joined string. */
        multi-select .ms-sel:not(:last-child)::after { content: ", "; }

        /* daisyUI's .btn:active applies `translate: 0 .5px` (a standalone CSS
           translate property, not `transform`) for its click-press effect -
           this control should look like the other select-styled inputs, not a button. */
        multi-select .btn:active { translate: 0 !important; }
    </style>
</multi-select>
